// One-shot commit-dialog resume trigger. After a guest signs in via the inline
// `EventsGuestAuthPanel`, they round-trip back to the event page with a
// `?commit=1` marker. Once client-side auth enrichment settles and the viewer
// can commit, this reopens the commit dialog exactly once, then strips the
// marker from the URL so a refresh/back-nav doesn't re-trigger it.
//
// Client-only: the resume is driven by client-side enrichment and a history
// replace — both no-ops during SSR.

import { ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { EventResponse } from '@@/types/api';
import { canCommit, type ViewerRole } from '~/utils/event-policy';

interface UseCommitResumeOptions {
  event: Ref<EventResponse | null>;
  viewerRole: Ref<ViewerRole>;
  isEnriching: Ref<boolean>;
  openCommitDialog: () => void;
}

export function useCommitResume(opts: UseCommitResumeOptions): void {
  const { event, viewerRole, isEnriching, openCommitDialog } = opts;

  if (!import.meta.client) return;

  const route = useRoute();
  const router = useRouter();

  // One-shot guard: the resume (or the marker cleanup when not committable)
  // fires at most once per page mount.
  const hasResumed = ref(false);

  function clearCommitParam() {
    const rest = { ...route.query };
    delete rest.commit;
    router.replace({ query: rest });
  }

  watch([() => route.query.commit, isEnriching, event, viewerRole], () => {
    if (hasResumed.value) return;
    if (route.query.commit !== '1') return;
    // Wait for auth enrichment to settle so the viewer role is trustworthy.
    if (isEnriching.value) return;
    // Wait for the event to load before deciding whether a commit is allowed.
    if (!event.value) return;

    hasResumed.value = true;
    if (canCommit(event.value, viewerRole.value)) {
      openCommitDialog();
    }
    // Always strip the marker — whether we resumed or the viewer turned out
    // not to be committable — so it doesn't linger or re-fire.
    clearCommitParam();
  });
}
